import React, { useState, useEffect } from 'react';
import { useHomepageSettings } from '../../hooks/useHomepageSettings';

interface Testimonial {
  clientName: string;
  clientTitle?: string;
  clientPhoto?: string;
  quote: string;
  rating: number;
}

const TestimonialsCarousel: React.FC = () => {
  const { data: settings, isLoading } = useHomepageSettings();

  const defaultHeading = 'What Our Clients Say';
  const defaultTestimonials: Testimonial[] = [
    {
      clientName: 'Michael & Sarah Johnson',
      clientTitle: 'Homeowners, Destin FL',
      quote:
        'OnArrival Global gives us complete peace of mind. After Hurricane season last year, they checked our property immediately and sent detailed photos. Their professionalism is unmatched.',
      rating: 5,
    },
    {
      clientName: 'Robert Martinez',
      clientTitle: 'Property Owner, 30A',
      quote:
        'The best decision we made was hiring OnArrival. They caught a small roof leak before it became a major problem. Saved us thousands in potential water damage!',
      rating: 5,
    },
    {
      clientName: 'Jennifer Williams',
      clientTitle: 'Vacation Home Owner',
      quote:
        'Their concierge service is exceptional. Our home is always perfectly prepared when we arrive, and they handle everything with care and attention to detail.',
      rating: 5,
    },
  ];

  const displayHeading = settings?.testimonials?.heading || defaultHeading;

  // Map Sanity testimonials to component format
  const sanityTestimonials: Testimonial[] =
    settings?.testimonials?.items?.map((item) => {
      const testimonial: Testimonial = {
        clientName: item.clientName,
        quote: item.quote,
        rating: item.rating,
      };
      if (item.clientRole) testimonial.clientTitle = item.clientRole;
      if (item.clientPhoto?.asset?.url)
        testimonial.clientPhoto = item.clientPhoto.asset.url;
      return testimonial;
    }) || [];

  const displayTestimonials =
    sanityTestimonials.length > 0 ? sanityTestimonials : defaultTestimonials;
  const isEnabled = settings?.testimonials?.enabled !== false;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % displayTestimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, displayTestimonials.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = displayTestimonials[currentIndex];

  // Don't render if disabled
  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary-50 via-blue-50 to-stone-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
        </div>
      </section>
    );
  }

  if (!currentTestimonial) {
    return null;
  }

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-primary-50 via-blue-50 to-stone-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4">
            {displayHeading}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 mx-auto rounded-full" />
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-white rounded-3xl shadow-2xl p-8 md:p-12 transition-all duration-500 hover:shadow-3xl">
            {/* Quote Icon */}
            <div className="absolute top-6 left-6 text-6xl text-primary-100 leading-none">
              &ldquo;
            </div>

            {/* Rating Stars */}
            <div className="flex justify-center mb-6">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <svg
                  key={i}
                  className="w-6 h-6 text-yellow-400 fill-current"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-xl md:text-2xl text-sand-800 text-center leading-relaxed mb-8 relative z-10">
              {currentTestimonial.quote}
            </blockquote>

            {/* Client Info */}
            <div className="flex flex-col items-center">
              {currentTestimonial.clientPhoto && (
                <img
                  src={currentTestimonial.clientPhoto}
                  alt={currentTestimonial.clientName}
                  className="w-16 h-16 rounded-full object-cover mb-4 border-4 border-primary-100"
                />
              )}
              <div className="text-center">
                <div className="font-bold text-lg text-primary-500">
                  {currentTestimonial.clientName}
                </div>
                {currentTestimonial.clientTitle && (
                  <div className="text-sand-700 text-sm">
                    {currentTestimonial.clientTitle}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-3 mt-8">
              {displayTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-primary-600 w-8'
                      : 'bg-sand-300 hover:bg-sand-400'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-between mt-8">
            <button
              onClick={() =>
                goToSlide(
                  (currentIndex - 1 + displayTestimonials.length) %
                    displayTestimonials.length
                )
              }
              className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:bg-primary-50 group"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-primary-700 group-hover:-translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                goToSlide((currentIndex + 1) % displayTestimonials.length)
              }
              className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:bg-primary-50 group"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-primary-700 group-hover:translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
